/**
 * runScheduler — side-effecting wrapper around the pure `catchUp(defs, today)`.
 *
 * Called from `App.tsx`:
 *   1. once on cold mount (after auth)
 *   2. on every `visibilitychange` → `visible`
 *
 * Responsibilities:
 *   - read `recurring.items` from the passed store handle, filter by `active`
 *   - call `catchUp` with `today = new Date()`
 *   - for each generated tx → `enqueue({type:'create_tx', payload})` and
 *     optimistically upsert into the local `transactions` slice
 *   - for each notification → `notifications.addNotification(...)`
 *   - for each processed def, advance `last_generated_date` via
 *     `enqueue({type:'update_recurring', payload:{id, rev, patch:{last_generated_date}}})`
 *
 * The `enqueue` callback is injected by the caller so the scheduler stays
 * decoupled from the sync wiring layer. App.tsx passes `enqueueOp` from
 * `syncWiring.ts` (the canonical write path that actually reaches the
 * queue worker); tests pass a `vi.fn()`. Going through the slice's
 * `sync.enqueue` directly is INTENTIONALLY not wired here — that path
 * only mutates local state and never reaches Sheets (see
 * `syncWiring.ts` "Sharp edges").
 *
 * Idempotence: a module-level `_lastRunYmd` records the last calendar day on
 * which we ran. Calling twice on the same day no-ops — visibilitychange may
 * fire twice in quick succession (tab → background → foreground in <1s), and
 * we don't want to flood the queue with duplicates. The catch-up math itself
 * also guards via `last_generated_date`, but defending in two layers keeps
 * the queue small in pathological visibility-flapping scenarios.
 *
 * Decoupled from zustand: takes anything that exposes the right shape, so
 * callers (and tests) can pass a plain object.
 */

import type {
  NotificationRow,
  RecurringRow,
  TransactionRow,
} from '@/lib/sheets/types';
import type { SyncOpInput } from '@/lib/sheets/sync-queue';
import { dayPart, joinDateTime } from '@/lib/format/datetime';
import {
  catchUp,
  type MonthlyDef,
  type RecurringDef,
  type WeeklyDef,
} from './scheduler';

/** Shape `runScheduler` reads from. Both the real zustand store and a
 *  fake test store satisfy this. */
export interface SchedulerStoreHandle {
  recurring: { items: RecurringRow[] };
  notifications: {
    addNotification: (n: NotificationRow) => void;
  };
  /** Read existing transactions to dedupe `(recurring_id, date)` pairs
   *  the catch-up has already produced — even when the def's stored
   *  `last_generated_date` is stale (e.g., the user reloaded before
   *  `update_recurring` drained off the queue). Without this guard the
   *  scheduler regenerates duplicate rows on every reload, corrupting
   *  the user's books — see runScheduler.test.ts "does not duplicate".
   *  `upsert` is the optimistic-write path. `items` is read-only. */
  transactions?: {
    items?: ReadonlyArray<TransactionRow>;
    upsert: (t: TransactionRow) => void;
  };
  /** Optional pending sync ops — needed because a freshly-generated tx
   *  may live ONLY in the queue (not yet on Sheets) when the user
   *  reloads. The queue persists to localStorage, so on reload we can
   *  ask "is there already a `create_tx` for this recurring_id+date
   *  waiting to flush?" and skip if so. */
  sync?: {
    pending: ReadonlyArray<{ type: string; payload: unknown }>;
  };
}

/** Canonical write path. The real `enqueueOp` from `syncWiring.ts` is
 *  passed by App.tsx; tests pass a `vi.fn()` so they can assert on the
 *  ops the scheduler emits. We deliberately do NOT use the slice's
 *  `sync.enqueue` here — that's local state only and the queue worker
 *  never observes it (see `syncWiring.ts` "Sharp edges"). */
export type EnqueueOp = (op: SyncOpInput) => void;

let _lastRunYmd: string | null = null;

/** Dedupe key — mirrors `scheduler.fmt` which is local-time. Using UTC
 *  here would let the dedupe straddle two local calendar days near
 *  midnight in non-UTC timezones. */
function ymdLocal(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function genId(prefix: string): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return `${prefix}-${crypto.randomUUID()}`;
  }
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function toDef(r: RecurringRow): RecurringDef {
  // Common shape (everything except the `freq`/`freq_day` discriminator pair).
  const base = {
    id: r.id,
    book_id: r.book_id,
    title: r.title,
    amount_minor: r.amount_minor,
    currency: r.currency,
    kind: r.kind,
    category_id: r.category_id,
    subcategory_id: r.subcategory_id || undefined,
    note: r.note || undefined,
    start_date: r.start_date,
    end_date: r.end_date || undefined,
    last_generated_date: r.last_generated_date || undefined,
    active: r.active,
  };
  // The branches MUST construct each variant explicitly so the resulting
  // discriminated-union member is well-formed at compile time. A flat
  // `{...base, freq: r.freq, freq_day: r.freq_day}` would widen everything
  // back to the base type and defeat the whole point of this section.
  switch (r.freq) {
    case 'monthly':
      // Sheets storage layer is `number`; the scheduler's `validate()`
      // (the runtime safety net) rejects out-of-range values. We cast to
      // the literal-union once at this boundary.
      return { ...base, freq: 'monthly', freq_day: r.freq_day as MonthlyDef['freq_day'] };
    case 'weekly':
      return { ...base, freq: 'weekly', freq_day: r.freq_day as WeeklyDef['freq_day'] };
    case 'daily':
      return { ...base, freq: 'daily', freq_day: 0 };
    case 'yearly':
      return { ...base, freq: 'yearly', freq_day: 0 };
  }
}

/**
 * Run the catch-up scheduler against the store. Idempotent within the
 * current calendar day: a second call no-ops.
 *
 * `today` is exposed for tests; production callers pass `new Date()`.
 */
export function runScheduler(
  store: SchedulerStoreHandle,
  enqueue: EnqueueOp,
  today: Date = new Date(),
): void {
  const todayYmd = ymdLocal(today);
  if (_lastRunYmd === todayYmd) {
    // Visibility flapped or we mounted twice. Bail.
    return;
  }

  const activeRows = store.recurring.items.filter((r) => r.active && !r.deleted_at);
  // Empty pass must NOT consume the per-day guard. SchedulerEffect mounts
  // the moment `isAuthed` flips true — BEFORE loadInitial populates
  // `recurring.items` — so the cold-mount pass usually sees an empty store.
  // Marking the day "done" here would no-op every later run (visibility
  // flip, post-bootstrap kick) and the whole calendar day generates
  // nothing; defs only materialize after midnight rolls the guard over.
  if (activeRows.length === 0) return;
  _lastRunYmd = todayYmd;

  // Index the original RecurringRows by id so we can pull each `rev` for
  // the `update_recurring` op (the queue's validator demands it).
  const rowById = new Map<string, RecurringRow>();
  for (const r of activeRows) rowById.set(r.id, r);

  // Build a (recurring_id → max-date) map from existing transactions so
  // the cursor seed honors what the books actually contain. Critical
  // because the def's `last_generated_date` lags behind any pending
  // `update_recurring` op — if the user reloads before the queue
  // drains, the on-sheet `last_generated_date` is still empty even
  // though a tx for today already exists, and a naive run would
  // regenerate the same row on every reload.
  //
  // We also walk `sync.pending` so a freshly-generated tx that only
  // lives in the queue (queue persists to localStorage; sheet write
  // not yet flushed) still counts as "already covered". Otherwise the
  // first reload between mount and queue-drain would produce a dup.
  const txItems = store.transactions?.items ?? [];
  const maxTxDateByRecurring = new Map<string, string>();
  // `last_generated_date` (and the cursor in `catchUp`) is YYYY-MM-DD.
  // v5 transaction `date` may carry a `THH:MM` suffix; without `dayPart`
  // we'd seed `last_generated_date` with `'2026-05-05T16:15'`, then
  // `parseDate` would NaN out, and the cursor would silently fall back
  // to `start_date` — regenerating every cycle from the beginning.
  const note = (rid: string, date: string): void => {
    if (!rid || !date) return;
    const ymd = dayPart(date);
    if (!ymd) return;
    const cur = maxTxDateByRecurring.get(rid) ?? '';
    if (ymd > cur) maxTxDateByRecurring.set(rid, ymd);
  };
  for (const t of txItems) {
    if (t.deleted_at) continue;
    note(t.recurring_id, t.date);
  }
  for (const op of store.sync?.pending ?? []) {
    if (op.type !== 'create_tx') continue;
    const p = op.payload as
      | { recurring_id?: string; date?: string }
      | null
      | undefined;
    if (p && typeof p.recurring_id === 'string' && typeof p.date === 'string') {
      note(p.recurring_id, p.date);
    }
  }
  // Overlay the existing-tx max date onto each def so catchUp seeds its
  // cursor at `max(stored_last_generated_date, latest_tx_date)`.
  const defs = activeRows.map((r) => {
    const def = toDef(r);
    const txMax = maxTxDateByRecurring.get(r.id);
    if (txMax && (!def.last_generated_date || txMax > def.last_generated_date)) {
      return { ...def, last_generated_date: txMax } as RecurringDef;
    }
    return def;
  });
  const result = catchUp(defs, today);

  const nowIso = new Date().toISOString();

  // Trigger HH:MM for this batch — every tx generated in THIS catch-up
  // pass shares the same suffix, even multi-day backfills. We don't try
  // to synthesize per-day "what time would this have run" because (a)
  // there's no historical truth to reconstruct and (b) a single shared
  // stamp keeps the dedup invariant simple: the same calendar day
  // always collapses to one HH:MM under `dayPart`.
  const triggerHHMM =
    `${String(today.getHours()).padStart(2, '0')}:${String(today.getMinutes()).padStart(2, '0')}`;

  for (const tx of result.generated) {
    // Generate the id locally so the optimistic local row and the queue
    // op share an id — without this the worker would write a separate row
    // and the optimistic one would orphan after reconciliation.
    const id = genId('tx');
    // v5: stamp `date` with the scheduler's trigger time so RecentTxList
    // renders a time chip and the row reads like a real "now" entry
    // rather than a midnight ghost. `dayPart()` in the dedup overlay
    // above keeps idempotency intact.
    const stampedDate = joinDateTime(tx.date, triggerHHMM);
    // note fallback: an empty `note` on a recurring def is the common
    // case (the user named it via `title`). Falling back to title makes
    // the resulting tx identifiable in the list ("Gogolook 每月薪資"
    // instead of a bare category-name row).
    const note = tx.note || rowById.get(tx.recurring_id)?.title || '';
    enqueue({
      type: 'create_tx',
      payload: {
        id,
        book_id: tx.book_id,
        date: stampedDate,
        amount_minor: tx.amount_minor,
        currency: tx.currency,
        kind: tx.kind,
        category_id: tx.category_id,
        subcategory_id: tx.subcategory_id,
        note,
        // v3: `project` is always '' on new writes; the queue validator
        // still requires the field to be present.
        project: '',
        tags: '',
        recurring_id: tx.recurring_id,
      },
    });
    // Optimistic local upsert — keeps the UI honest while the worker
    // drains. Mirrors the OCR flow.
    if (store.transactions) {
      const row: TransactionRow = {
        id,
        book_id: tx.book_id,
        date: stampedDate,
        amount_minor: tx.amount_minor,
        currency: tx.currency,
        kind: tx.kind,
        category_id: tx.category_id,
        subcategory_id: tx.subcategory_id,
        note,
        project: '',
        tags: '',
        recurring_id: tx.recurring_id,
        rev: 1,
        created_at: nowIso,
        updated_at: nowIso,
        deleted_at: '',
      };
      store.transactions.upsert(row);
    }
  }

  for (const n of result.notifications) {
    const id = genId('n');
    const created_at = new Date().toISOString();
    // Optimistic local insert — the bell badge updates immediately.
    const row: NotificationRow = {
      id,
      kind: 'recurring_generated',
      payload: JSON.stringify(n.payload),
      read_at: '',
      created_at,
    };
    store.notifications.addNotification(row);
    // Persist to the sheet so the notification survives a reload.
    // Without this, recurring_generated notifications evaporated whenever
    // `loadInitial` re-pulled an empty notifications tab — bell badge
    // came up briefly during the catch-up tick and disappeared on next
    // refresh. We pass the same id + created_at so reconciliation
    // doesn't surface a duplicate when realtimePoll / the next bootstrap
    // re-reads the sheet.
    enqueue({
      type: 'create_notification',
      payload: {
        id,
        created_at,
        kind: 'recurring_generated',
        payload: { ...n.payload } as Record<string, unknown>,
      },
    });
  }

  // Update last_generated_date for every def the scheduler processed.
  // The queue's `update_recurring` validator requires `{id, rev, patch}`;
  // we pull `rev` from the active row we already loaded.
  for (const [recurringId, lastGen] of Object.entries(result.nextLastGeneratedDate)) {
    const r = rowById.get(recurringId);
    if (!r) continue;
    enqueue({
      type: 'update_recurring',
      payload: {
        id: recurringId,
        rev: r.rev,
        patch: { last_generated_date: lastGen },
      },
    });
  }
}

/** Test helper: reset the dedupe guard. Not exported from index. */
export function __resetSchedulerLastRun(): void {
  _lastRunYmd = null;
}

/**
 * Force a fresh scheduler pass — bypasses the per-day dedupe. Call this
 * after CRUD on `recurring` defs so a newly created or just-edited row
 * generates today's catch-up entry without waiting for tomorrow / a tab
 * visibility flip / a manual reload.
 */
export function kickScheduler(
  store: SchedulerStoreHandle,
  enqueue: EnqueueOp,
  today?: Date,
): void {
  _lastRunYmd = null;
  runScheduler(store, enqueue, today ?? new Date());
}
